import { useId, useRef } from 'react';
import styled from 'styled-components';
import { IconButton } from '@ardoq/button';
import { IconName } from '@ardoq/icons';
import { t, type I18nLocale } from '@ardoq/i18n';
import { Popover2025 } from '@ardoq/popovers';
import { Box } from '@ardoq/layout';
import { PeekNotificationBanner } from '@ardoq/status-ui';
import {
  type GiveContextStep,
  type OrgDocumentsPickerView,
} from '../assistantDocumentsOperations';
import { MAX_DOCUMENTS_LIMIT } from '../../constants';
import { AnimatedPopoverSurface } from './AnimatedPopoverSurface';
import { GiveContextMenu } from './GiveContextMenu';
import {
  type DocumentSearchProps,
  type DocumentSelectionProps,
  OrgDocumentsPicker,
} from './OrgDocumentsPicker';

const STEP_WIDTH: Record<GiveContextStep, number> = {
  menu: 280,
  orgDocuments: 320,
};

export const GIVE_CONTEXT_ANCHOR_ID = 'assistant-give-context-anchor';

const AnchorTarget = styled.span`
  display: inline-flex;
`;

export type GiveContextProps = DocumentSelectionProps & {
  i18nLocale: I18nLocale;
  isOpen: boolean;
  step: GiveContextStep;
  orgDocumentsView: OrgDocumentsPickerView;
  search: DocumentSearchProps;
  onOpen: VoidFunction;
  onClose: VoidFunction;
  onShowOrgDocuments: VoidFunction;
  onUploadFromComputer: (files: File[]) => void;
  webSearchEnabled: boolean;
  hasWebSearchFeature: boolean;
  onToggleWebSearch: (enabled: boolean) => void;
};

type GiveContextButtonProps = GiveContextProps & {
  isRequestPending: boolean;
  isDisabled: boolean;
};

export const GiveContextButton = ({
  i18nLocale,
  isOpen,
  step,
  orgDocumentsView,
  search,
  onOpen,
  onClose,
  onShowOrgDocuments,
  onUploadFromComputer,
  selectedDocumentIds,
  isSelectionLimitReached,
  onSelectDocument,
  onDeselectDocument,
  webSearchEnabled,
  hasWebSearchFeature,
  onToggleWebSearch,
  isRequestPending,
  isDisabled,
}: GiveContextButtonProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverId = useId();

  return (
    <>
      <AnchorTarget id={GIVE_CONTEXT_ANCHOR_ID}>
        <IconButton
          ref={triggerRef}
          iconName={IconName.ADD}
          label={t(
            i18nLocale,
            'assistant:documents.giveContext',
            'Give context'
          )}
          isActive={isOpen}
          isDisabled={isDisabled}
          onClick={isOpen ? onClose : onOpen}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls={popoverId}
        />
      </AnchorTarget>
      <Popover2025
        id={popoverId}
        role="dialog"
        aria-label={t(
          i18nLocale,
          'assistant:documents.giveContext',
          'Give context'
        )}
        isOpen={isOpen}
        anchorRef={triggerRef}
        onClose={onClose}
        onClickOutside={onClose}
        placement={{ side: 'top', overflow: 'right' }}
      >
        <Box position="relative">
          <PeekNotificationBanner
            isVisible={step === 'orgDocuments' && isSelectionLimitReached}
            status="subtle"
          >
            {t(
              i18nLocale,
              'assistant:documents.selectionLimitReached',
              { count: MAX_DOCUMENTS_LIMIT },
              'You can add up to {{count}} documents. Deselect one to add another.'
            )}
          </PeekNotificationBanner>
          <Box position="relative" zIndex={1}>
            <AnimatedPopoverSurface width={STEP_WIDTH[step]}>
              {step === 'menu' ? (
                <GiveContextMenu
                  i18nLocale={i18nLocale}
                  isSelectionLimitReached={isSelectionLimitReached}
                  onSelectOrgDocuments={onShowOrgDocuments}
                  onUploadFromComputer={onUploadFromComputer}
                  webSearchEnabled={webSearchEnabled}
                  hasWebSearchFeature={hasWebSearchFeature}
                  isRequestPending={isRequestPending}
                  onToggleWebSearch={onToggleWebSearch}
                />
              ) : (
                <OrgDocumentsPicker
                  i18nLocale={i18nLocale}
                  orgDocumentsView={orgDocumentsView}
                  search={search}
                  selectedDocumentIds={selectedDocumentIds}
                  isSelectionLimitReached={isSelectionLimitReached}
                  onSelectDocument={onSelectDocument}
                  onDeselectDocument={onDeselectDocument}
                />
              )}
            </AnimatedPopoverSurface>
          </Box>
        </Box>
      </Popover2025>
    </>
  );
};
